import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import { useNavigate } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { may, useActiveTenant } from '@/tenant/context'
import { Button } from '@/ui/Button'
import { Field, TextField } from '@/ui/Field'
import { Empty, ErrorState, InlineError, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

type RoleRow = Schema<'RoleOut'>

/** Mirrors `InviteRequest.note` (`max_length=1000`), enforced before submit. */
const NOTE_MAX = 1000

/**
 * Invite someone — `shell.html#invite`, the 36rem card on a screen of its own
 * (parity P-INV-01, the owner's choice of 2026-09-13 between the two shapes
 * the mockups draw). The Team panel's button leads here; *Cancel* and a sent
 * invitation both lead back.
 *
 * The role is chosen with its permissions visible rather than from a bare
 * dropdown — the screen's own note: most wrong-permission incidents come from
 * somebody picking a name that sounded standard. The organisation's own roles
 * are the radios (SHELL-26), with how many pages each opens beneath its name.
 *
 * **There is no administrator option and there cannot be**: an emailed link
 * that could confer the organisation would make control of a mailbox
 * equivalent to it, so the server takes a role id and the list has none.
 *
 * The note (SHELL-27) is quoted and attributed in the email and stored on the
 * invitation so that *Resend* reproduces the same message; it is not readable
 * from the team list, because a message one person wrote to another is not
 * the organisation's to read.
 */
export function InvitePage(): JSX.Element {
  const { tenant, membership } = useActiveTenant()
  const navigate = useNavigate()
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [chosen, setChosen] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [emailMissing, setEmailMissing] = useState(false)

  const roles = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/roles', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  const invite = useMutation((body: { email: string; role_id: string; note: string | null }) =>
    call(() =>
      api.POST('/api/v1/tenants/{tenant_id}/invitations', {
        params: { path: { tenant_id: tenant.id } },
        body,
      }),
    ),
  )

  const list: RoleRow[] = roles.state.data?.roles ?? []
  const roleId = chosen ?? list[0]?.id ?? null

  function back(): void {
    void navigate('/settings/team')
  }

  async function onSubmit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    const address = email.trim().toLowerCase()
    if (address.length === 0) {
      setEmailMissing(true)
      return
    }
    if (roleId === null) return
    const result = await invite.run({ email: address, role_id: roleId, note: note.trim() || null })
    if (!result.ok) return
    toast.show(
      result.data.invitation_email_sent
        ? { kind: 'success', title: 'Invitation sent', body: `${address} has seven days.` }
        : {
            kind: 'warning',
            title: 'Invitation created, email not sent',
            body: `The invitation for ${address} exists. Withdraw and re-invite to try the email again.`,
          },
    )
    back()
  }

  if (!may(membership, 'members:invite')) {
    return (
      <div style={{ maxWidth: '36rem', margin: '0 auto' }}>
        <div className="ar-card">
          <Empty
            icon="✉"
            title="Your role cannot invite people"
            body="Ask an owner to send the invitation."
            action={
              <Button tone="ghost" size="sm" onClick={back}>
                Back to the team
              </Button>
            }
          />
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '36rem', margin: '0 auto' }}>
      <form
        className="ar-card"
        noValidate
        onSubmit={(event) => {
          void onSubmit(event)
        }}
      >
        <div className="ar-card__header">
          <h2>Invite someone</h2>
        </div>
        <div className="ar-card__body ar-stack">
          <InlineError failure={invite.state.failure} />
          <TextField
            label="Email address"
            type="email"
            required
            autoComplete="off"
            help="They'll get a link that expires in 7 days."
            value={email}
            error={emailMissing ? 'Enter the address to invite.' : undefined}
            onChange={(event) => {
              setEmail(event.target.value)
              setEmailMissing(false)
              invite.reset()
            }}
          />

          {roles.state.status === 'loading' ? <Loading label="Loading roles" lines={3} /> : null}
          {roles.state.status === 'error' ? (
            <ErrorState
              failure={roles.state.failure}
              title="Could not load this organisation's roles"
              onRetry={roles.refetch}
            />
          ) : null}
          {roles.state.status === 'ready' ? (
            list.length === 0 ? (
              // Not the mockup's state — it draws three roles — but a new
              // organisation has none, and a radiogroup with no radios says
              // nothing. Everybody invited holds a role.
              <p className="ar-field__help" style={{ margin: 0 }}>
                This organisation has no roles yet. Create one first — everybody invited holds one,
                and it is what decides which screens they can open.
              </p>
            ) : (
              <div role="radiogroup" aria-label="Role">
                {list.map((option) => (
                  <label className="ar-choice ar-choice--radio" key={option.id}>
                    <input
                      type="radio"
                      name="role"
                      value={option.id}
                      checked={roleId === option.id}
                      onChange={() => {
                        setChosen(option.id)
                      }}
                    />
                    <span className="ar-choice__box" aria-hidden="true" />
                    <span className="ar-choice__text">
                      {option.name}
                      <span className="ar-choice__desc">
                        {option.pages.length === 0
                          ? 'No pages yet — they would see nothing.'
                          : `Can open ${String(option.pages.length)} page${option.pages.length === 1 ? '' : 's'}.`}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )
          ) : null}

          <Field label="Add a note (optional)">
            {(aria) => (
              <textarea
                className="ar-textarea"
                {...aria}
                rows={3}
                maxLength={NOTE_MAX}
                value={note}
                placeholder="Meera — you'll be approving the blog drafts."
                onChange={(event) => {
                  setNote(event.target.value)
                  invite.reset()
                }}
              />
            )}
          </Field>
        </div>
        <div className="ar-card__footer">
          <span className="ar-card__spacer" />
          <Button tone="ghost" onClick={back}>
            Cancel
          </Button>
          <Button
            tone="primary"
            type="submit"
            writes
            busy={invite.pending}
            disabled={roles.state.status !== 'ready' || list.length === 0}
          >
            Send invite
          </Button>
        </div>
      </form>
    </div>
  )
}
